import { Service, TenantBusiness } from "@turizi/shared";
import { Link } from "react-router-dom";
import { SERVICES_PATH } from "@/lib/navigation";
import { ServiceTitle, ServiceTitleStatic } from "./services/service-title";
import { TagIcon } from "@/lib/tag-utils";
import { useTranslation } from "react-i18next";
import { MoneyIn } from "./snippets";
import { Badge } from "@mantine/core";
import { applyDiscount, formatDayShort, formatPrice } from "@/lib/format-utils";
import { getServiceSellPrice } from "@/lib/models-utils";

interface Props {
    service: Service;
    onSelect?: (service: Service) => void;
    start_day?: string;
    className?: string;
    showProviders?: boolean;
    client?: TenantBusiness;
}


export default function ServiceCardPublic({
    service,
    start_day,
    onSelect,
    client,
    className,
}: Props) {
    const { t } = useTranslation();
    const price = getServiceSellPrice(service, start_day)?.[0];
    const availability = service?.availability;

    if (!service || !service?.category) {
        return null;
    }

    const getDiscountPercentage = (currency: string): number | null => {
        const clientDiscountCategory = client?.discount_category;
        if (!clientDiscountCategory || !service.discounts) {
            return null;
        }
        const discountCategory = service.discounts.find(
            discount => discount.category === clientDiscountCategory
        );
        if (!discountCategory || !discountCategory.discounts.length) {
            return null;
        }
        return discountCategory.discounts.find(discount => discount.currency === currency)?.percentage || null;
    };

    const discountPercentage = getDiscountPercentage(price?.default_currency || '--');

    return (
        <div
            key={service.id}
            onClick={() => onSelect?.(service)}
            className={className}
        >
            <div className="lg:flex items-center gap-4 space-y-2 lg:space-y-0">
                <TagIcon name={service.category} hideText noBackground />
                <div className="flex-1 space-y-2">
                    <ServiceTitle
                        service={service}
                        hideProviders={true}
                        links={false} />
                    {service?.child_services?.map(child_service => (
                        <div
                            key={child_service.id}
                            className="text-gray-400 text-xxs"
                        >
                            #{child_service.id} - {ServiceTitleStatic(child_service)}
                        </div>
                    ))}
                    {/* Availability Status Badge */}
                    {availability?.daily_available !== undefined && (
                        <div className="mb-0.5">
                            {availability.daily_available > 0 ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[10px] font-semibold text-green-600">
                                    {t('available_places')}: {availability.daily_available}
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[10px] font-semibold text-red-600">
                                    {t('no_places_available')}
                                </span>
                            )}
                        </div>
                    )}
                </div>
                <div className="flex flex-col items-end justify-start gap-2">
                    {/* Price and Details */}
                    <div className="text-right flex flex-col items-end">
                        {price && discountPercentage ? (
                            <div className="flex flex-col items-end">
                                {/* Original Price & Discount Badge */}
                                <div className="flex items-center gap-1.5 mb-0.5">
                                    <span className="text-xxs text-gray-400 font-medium ">
                                        $ {formatPrice(price.default_amount)} {price.default_currency}
                                    </span>
                                    <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[10px] font-bold bg-green-50 text-green-700 border border-green-200">
                                        -{discountPercentage}%
                                    </span>
                                </div>
                                {/* Final Price */}
                                <div className="text-base font-extrabold text-green-600">
                                    $ {formatPrice(applyDiscount(price.default_amount, discountPercentage))}{' '}
                                    <span className="text-xs font-semibold text-gray-500">{price.default_currency}</span>
                                </div>
                                {/* Unit */}
                                <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider mt-0.5">
                                    {t(`price_by_${service.price_by}`)}
                                </div>
                                {/* Validity dates */}
                                {price.start_day && (
                                    <div className="text-[9px] text-gray-400 italic">
                                        {formatDayShort(price.start_day)}
                                        {price.end_day && ` - ${formatDayShort(price.end_day)}`}
                                    </div>
                                )}
                            </div>
                        ) : price ? (
                            <div className="flex flex-col items-end">
                                {/* Final Price */}
                                <div className="text-base font-extrabold text-gray-800">
                                    $ {formatPrice(price.default_amount)}{' '}
                                    <span className="text-xs font-semibold text-gray-500">{price.default_currency}</span>
                                </div>
                                {/* Unit */}
                                <div className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider mt-0.5">
                                    {t(`price_by_${service.price_by}`)}
                                </div>
                                {/* Validity dates */}
                                {price.start_day && (
                                    <div className="text-[9px] text-gray-400 italic">
                                        {formatDayShort(price.start_day)}
                                        {price.end_day && ` - ${formatDayShort(price.end_day)}`}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-xs text-gray-400 italic">
                                {t('no_price_available')}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}